import { useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { orpc } from '../lib/orpc.js';
import { current_language } from '../i18n/index.js';
import { friendly_error } from '../lib/friendly-error.js';

export function ChitsNewPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const lang = current_language();

  const [name_ta, set_name_ta] = useState('');
  const [name_en, set_name_en] = useState('');
  const [pool_size, set_pool_size] = useState(20);
  const [duration_months, set_duration_months] = useState(20);
  const [monthly_rupees, set_monthly_rupees] = useState(5000);
  const [commission_pct, set_commission_pct] = useState(5);
  const [start_date, set_start_date] = useState(new Date().toISOString().slice(0, 10));
  const [collection_day, set_collection_day] = useState(10);

  const m = useMutation({
    mutationFn: async () =>
      orpc.chits.create({
        name_ta,
        name_en,
        pool_size,
        duration_months,
        monthly_subscription_paise: monthly_rupees * 100,
        foreman_commission_pct: commission_pct,
        start_date: new Date(start_date).toISOString(),
        collection_day,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['chits'] });
      navigate('/chits');
    },
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    m.mutate();
  };

  const label_cls = `block mb-1 text-sm ${lang === 'ta' ? 'font-tamil' : 'font-sans'}`;
  const input_cls =
    'w-full px-3 py-2 border border-line rounded bg-bg-elev focus:outline-none focus:ring-2 focus:ring-primary/40';

  return (
    <div>
      <h1 className="text-2xl font-tamil text-primary mb-6">{t('chits.create_title')}</h1>
      <form onSubmit={submit} className="bg-surface border border-line rounded-lg p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <label>
          <span className={label_cls}>{t('chits.fields.name_ta')}</span>
          <input
            className={`${input_cls} font-tamil`}
            value={name_ta}
            onChange={(e) => set_name_ta(e.target.value)}
            placeholder="பொன் கோல்ட் 24A"
            required
          />
        </label>
        <label>
          <span className={label_cls}>{t('chits.fields.name_en')}</span>
          <input
            className={input_cls}
            value={name_en}
            onChange={(e) => set_name_en(e.target.value)}
            placeholder="Pon Gold 24A"
            required
          />
        </label>
        <label>
          <span className={label_cls}>{t('chits.fields.pool_size')}</span>
          <input
            type="number"
            min={2}
            max={20}
            className={input_cls}
            value={pool_size}
            onChange={(e) => set_pool_size(Number(e.target.value))}
            required
          />
        </label>
        <label>
          <span className={label_cls}>{t('chits.fields.duration_months')}</span>
          <input
            type="number"
            min={2}
            max={60}
            className={input_cls}
            value={duration_months}
            onChange={(e) => set_duration_months(Number(e.target.value))}
            required
          />
        </label>
        <label>
          <span className={label_cls}>{t('chits.fields.monthly_subscription')}</span>
          <div className="flex items-center gap-2">
            <span className="text-ink/60 text-sm">₹</span>
            <input
              type="number"
              min={1}
              className={`${input_cls} font-mono`}
              value={monthly_rupees}
              onChange={(e) => set_monthly_rupees(Number(e.target.value))}
              required
            />
          </div>
        </label>
        <label>
          <span className={label_cls}>{t('chits.fields.foreman_commission_pct')}</span>
          <input
            type="number"
            min={0}
            max={20}
            step={0.5}
            className={input_cls}
            value={commission_pct}
            onChange={(e) => set_commission_pct(Number(e.target.value))}
          />
        </label>
        <label>
          <span className={label_cls}>{t('chits.fields.start_date')}</span>
          <input
            type="date"
            className={input_cls}
            value={start_date}
            onChange={(e) => set_start_date(e.target.value)}
            required
          />
        </label>
        <label>
          <span className={label_cls}>{t('chits.fields.collection_day')}</span>
          <input
            type="number"
            min={1}
            max={28}
            className={input_cls}
            value={collection_day}
            onChange={(e) => set_collection_day(Number(e.target.value))}
            required
          />
        </label>
        {m.error && (
          <div className="md:col-span-2 px-3 py-2 bg-bad-soft text-bad text-xs rounded">
            {friendly_error(m.error)}
          </div>
        )}
        <div className="md:col-span-2 flex gap-2 justify-end pt-2">
          <button
            type="button"
            onClick={() => navigate('/chits')}
            className="px-4 py-2 rounded border border-line text-ink/70 hover:bg-bg-elev text-sm"
          >
            {t('actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={m.isPending}
            className="bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white px-4 py-2 rounded text-sm font-medium"
          >
            {t('actions.create')}
          </button>
        </div>
      </form>
    </div>
  );
}
